import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import {
  API_USER_HMAC,
  ApiService,
  ListAPIResponse,
  USER_JWT,
  USER_PROFILE,
  USER_URL_BY_ID_V5,
  USER_URL_V5,
} from '../core/api.service';
import { IUserIDs, Permissions, User } from '../shared/model/user';
import { UserPayload } from '../shared/model/user-company.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private api: ApiService) {}

  getUserHmacHash(app: string): Observable<string> {
    return this.api
      .get<{ hash: string }>(API_USER_HMAC(app))
      .pipe(map((result) => result?.hash));
  }

  getUser(userId: string, companyId: string): Observable<User> {
    return this.api
      .get<User>(USER_URL_BY_ID_V5(companyId, userId))
      .pipe(map((u) => new User(u)));
  }

  getCompleteProfile(companyId: string): Observable<User> {
    return this.api.get<User>(USER_JWT(companyId)).pipe(
      filter(() => !!companyId),
      map((u) => new User(u))
    );
  }

  getProfile(): Observable<User> {
    return this.api.get(USER_PROFILE).pipe(map((user) => new User(user)));
  }

  removeUser(userId: string, companyId: string): Observable<User> {
    return this.api.delete(USER_URL_BY_ID_V5(companyId, userId));
  }

  getUsers(
    companyId: string,
    objPerPage: number,
    page: number,
    query?: string,
    roles?: string[]
  ): Observable<ListAPIResponse<User>> {
    let params = new HttpParams()
      .set('obj_per_page', objPerPage)
      .set('page', page);

    if (!!query) {
      params = params.set('query', query);
    }

    if (!!roles?.length) {
      roles.forEach((role: string) => (params = params.append('roles', role)));
    }

    return this.api.get<ListAPIResponse<User>>(USER_URL_V5(companyId), {
      params,
    });
  }

  createUser(payload: UserPayload, companyId: string): Observable<IUserIDs> {
    return this.api.post(USER_URL_V5(companyId), payload);
  }

  updateUser(
    id: string,
    payload: Omit<UserPayload, 'email'>,
    companyId: string
  ): Observable<object> {
    return this.api.put(USER_URL_BY_ID_V5(companyId, id), payload);
  }

  // TODO support resources
  hasPermissions(permissions: string[], userPermissions: Permissions): boolean {
    return permissions?.every(
      (required) => !!userPermissions?.permissions[required]
    );
  }
}
